<template>
    <div class="w-full flex flex-col">
      <div v-if="conversationSupplier && !sameSupplier" class="w-full flex items-center pb-5 pt-4">
        <Avatar
          v-if="!_.isEmpty(conversationSupplier) && conversationSupplier?.name"
          :src="conversationSupplier?.logo"
          class="cursor-pointer"
          :title="conversationSupplier?.name"
          loading="lazy"
          @click="navigate"
        />
        <Link
          :href="translatePath(ROUTES.CODE.SUPPLIER, slugify(conversationSupplier.id, conversationSupplier.name))"
          class="text-primary-dark hover:text-primary-dark-hover cursor-pointer hover:underline ml-4"
        >
          {{ conversationSupplier.name }}
        </Link>
        <div class="flex-1 border-t border-primary-neutral ml-4" />
      </div>
       <div v-if="conversationSupplier && !sameSupplier" class="mb-5" @click="() => checkAuth()">
        <div class="flex flex-row items-start justify-between space-x-2">
          <div class="relative w-full">
            <TextArea
              v-model="comment"
              :name="`comment-${item.id}`"
              variant="outline"
              :placeholder="$t('text-write-message')"
              class-name="w-full"
              :rows="2"
              :min-height="64"
              :input-class-name="auth?.idSupplier ? 'pe-16' : 'pe-8'"
              :on-submit="handleSubmit"
              @cursor-changed="onCursorChanged"
            />
            <ChatEmojiPicker
              v-model="comment"
              :class="[windowSize?.width?.value < 1024 ? 'top-2' : 'top-3', auth.isLoggedIn ? 'end-10' : 'end-2']"
              :cursor-position="cursorPosition"
              @cursor-changed="onCursorChanged"
            />
            <ChatActionMenu
              v-if="auth?.isLoggedIn"
              v-model="comment"
              :name="`comment-${item.id}`"
              :images="images"
              :class="windowSize?.width?.value < 1024 ? 'top-2' : 'top-3'"
              :cursor-position="cursorPosition"
              @cursor-changed="onCursorChanged"
            />
          </div>
          <Button
            variant="submit"
            :loading="sending"
            :disabled="!sendingEnabled"
            :title="$t('text-send')"
            :size="windowSize?.width?.value < 1024 ? 'small' : 'medium'"
            :on-click="handleSubmit"
          >
            <SendIcon :class="windowSize?.width?.value < 1024 ? 'w-4 h-4' : 'w-6 h-6'" />
          </Button>
        </div>
        <div v-if="!!images.length" class="flex flex-wrap mt-2">
          <div
            v-for="(file, idx) in images"
            :key="idx"
            class="inline-flex flex-col overflow-hidden mt-2 me-2 relative"
          >
            <div class="flex-none w-24 h-24 sm:w-48 sm:h-48 mr-2 mb-2">
              <img :src="file.url" class="rounded object-cover w-24 h-24 sm:w-48 sm:h-48" loading="lazy" >
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

<script setup>
import * as Sentry from "@sentry/vue";
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { CHAT_MESSAGE_TYPE, STATUS_BLOCKED } from "@utils/constants";
import { useAuthStore } from '@stores/auth';
import { useCustomerStore } from '@stores/customer';
import Link from "@components/ui/link/link";
import Avatar from "@components/ui/avatar";
import SendIcon from "@components/icons/send-icon";
import TextArea from "@components/ui/text-area";
import Button from "@components/ui/button";
import ChatActionMenu from "@components/chat/chat-action-menu";
import ChatEmojiPicker from "@components/chat/chat-emoji-picker";

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  prevItem: {
    type: Object,
    default: () => {}
  },
  postError: {
    type: String,
    default: ''
  },
  isAddBlocked: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const config = useRuntimeConfig();
const auth = useAuthStore();
const customer = useCustomerStore();
const sending = ref(false);
const images = ref([]);
const comment = ref('');
const cursorPosition = ref(0);

const windowSize = useWindowSize();

const getParticipantIdsWithoutMe = (message) => {
  const ids = [message?.supplier?.id, message?.toSupplier?.id]
    .filter(Boolean);

  if (!auth.idSupplier) {
    return ids.sort((a, b) => a - b);
  }

  return ids
    .filter((id) => id !== auth.idSupplier)
    .sort((a, b) => a - b);
};

const getConversationSupplier = (message) => {
  if (!message) {
    return null;
  }

  const counterpartIds = getParticipantIdsWithoutMe(message);
  const counterpartId = counterpartIds[0];

  if (!counterpartId) {
    return message?.toSupplier || message?.supplier || null;
  }

  if (message?.supplier?.id === counterpartId) {
    return message.supplier;
  }

  if (message?.toSupplier?.id === counterpartId) {
    return message.toSupplier;
  }

  return message?.supplier || message?.toSupplier || null;
};

const conversationSupplier = computed(() => getConversationSupplier(props.item));
const currentConversationKey = computed(() => getParticipantIdsWithoutMe(props.item).join('-'));
const prevConversationKey = computed(() => getParticipantIdsWithoutMe(props.prevItem).join('-'));

const sameSupplier = computed(() => !!currentConversationKey.value && currentConversationKey.value === prevConversationKey.value);

const addMessage = (data) => {
  $eventBus.emit('message:add', {
      id: data.supplier.id,
      type: CHAT_MESSAGE_TYPE.SUPPLIER,
      message: data
    });
}

const sendingEnabled = computed(() => {
  return auth.isLoggedIn && !sending.value && (!!comment.value.length || !!images.value.length);
});

const fetchUrl = computed(() => config?.public?.appBaseUrl + API_ENDPOINTS.SUPPLIER_ID_MESSAGE.replace('{idSupplier}', conversationSupplier.value?.id));

const reset = () => {
    comment.value = '';
    images.value = [];
}

const handleSubmit = async () => {
  if (props.isAddBlocked) {
    $toast.error(t('text-add-message-blocked-notice'));
    return;
  }
  sending.value = true;
  if (customer?.status === STATUS_BLOCKED || (auth?.idSupplier && customer?.supplier?.status === STATUS_BLOCKED)) {
    // $toast.error(t('customer.blocked.error'));
    return;
  }
  const body = {
    content: comment.value,
    images: images.value.map((image) => ({
      filename: image.name,
      content: image.encoded
    }))
  }
  try {
    const data = await $fetch(fetchUrl.value, {
      method: 'POST',
      body
    });
    reset();
    addMessage(data);
  } catch (error) {
    if (import.meta.client) {
      $toast.error(t(props.postError));
    }
    Sentry.captureException(error);
  }
  sending.value = false;
}

const closeModal = () => {
  $eventBus.emit('modal:close');
}

const navigate = async () => {
  closeModal();
  await navigatorTo(translatePath(ROUTES.CODE.SUPPLIER, slugify(props.item?.supplier?.id, props.item?.supplier?.name)));
}

const onCursorChanged = (value) => {
  cursorPosition.value = value;
}
</script>
